import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { getErrorMessage } from '@/lib/apiErrors';


const loginSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string()
    .min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export const Login: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
    clearErrors,
  } = useForm<LoginForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Redirect to root if already authenticated
  // Check for both isAuthenticated AND user to avoid race conditions with stale state
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      console.log('User already authenticated, redirecting to home');
      navigate({ to: '/' });
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const onSubmit = async (rawData: Record<string, unknown>) => {
    try {
      setIsSubmitting(true);
      setError('');
      clearErrors();

      // Manually validate with Zod to catch and handle errors properly
      const validationResult = loginSchema.safeParse(rawData);
      
      if (!validationResult.success) {
        // Handle validation errors manually without throwing
        validationResult.error.issues.forEach((err) => {
          if (err.path[0] === 'email') {
            setFormError('email', { type: 'manual', message: err.message });
          } else if (err.path[0] === 'password') {
            setFormError('password', { type: 'manual', message: err.message });
          }
        });
        return; // Stop execution without throwing
      }

      const data = validationResult.data;
      await login(data);

      // Redirect to home page which will redirect to appropriate helpdesk
      navigate({ to: '/' });
    } catch (err) {
      console.error('Login error:', err);
      // login() rejects with the server's reason: wrong credentials, a locked account, or a connection problem
      setError(getErrorMessage(err, 'Sign in failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: Record<string, unknown>) => {
    // This should not be called now since we handle validation manually
    if (import.meta.env.DEV) {
      console.log('Form submission errors (should not happen):', errors);
    }
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4 sm:mb-6">
            <Logo className="h-16 sm:h-20 md:h-24" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Welcome back</h2>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Sign in to access your support dashboard
          </p>
        </div>

        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
            <CardTitle className="text-lg sm:text-xl">Sign in</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Enter your email and password to continue
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
            <CardContent className="space-y-4 px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  {...register('email')}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  {...register('password')}
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4 px-4 sm:px-6 pt-4 sm:pt-6 pb-4 sm:pb-6">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || isLoading}
              >
                {(isSubmitting || isLoading) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              <div className="text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </Link>
              </div>

              <div className="text-center text-xs sm:text-sm">
                <Link
                  to="/"
                  className="text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  &larr; Back to website
                </Link>
              </div>

            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};